import React, {useState, useEffect, useMemo, useRef, useCallback} from 'react';
import {
    List,
    useDataProvider,
    useListContext,
    useNotify,
    useRefresh,
    useTranslate,
    usePermissions,
} from 'react-admin';
import {Avatar, Box, Checkbox, Pagination, Typography} from '@mui/material';
import {
    ThumbUpAltOutlined,
    ThumbDownAltOutlined,
    PlayCircleOutline,
    TextSnippet,
    KeyboardOutlined,
} from '@mui/icons-material';
import moment from 'moment';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import StatusPill from '../components/statamic/StatusPill';
import ApprovalDialog from '../components/statamic/ApprovalDialog';
import FilterBar from '../components/statamic/FilterBar';
import EmptyState from '../components/statamic/EmptyState';
import {TaskAltOutlined} from '@mui/icons-material';
import {axiosApiInstance} from '../AxiosUtils';
import ModerationPostDialog from './ModerationPostDialog';

const MediaThumb = ({post}) => {
    const isText = post.message_type === 'TEXT';
    const isVideo = post.media_type === 'video' || post.message_type === 'VIDEO';
    const isPhoto = post.media_type === 'photo' || post.message_type === 'PHOTO';
    const mediaUrl = (isPhoto || isVideo)
        ? `${process.env.REACT_APP_CDN_URL}/${post.thumbnail}`
        : null;

    return (
        <Box
            sx={{
                position: 'relative',
                width: 120,
                height: 90,
                flexShrink: 0,
                borderRadius: `${t.radius.sm}px`,
                overflow: 'hidden',
                backgroundColor: isText ? t.color.sidebarHover : '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {isText && <TextSnippet sx={{fontSize: 22, color: t.color.text.muted}}/>}
            {mediaUrl && !isText && (
                <Box
                    component="img"
                    src={mediaUrl}
                    alt=""
                    sx={{width: '100%', height: '100%', objectFit: 'cover'}}
                />
            )}
            {isVideo && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.25)',
                    }}
                >
                    <PlayCircleOutline sx={{fontSize: 32, color: 'rgba(255,255,255,0.95)'}}/>
                </Box>
            )}
        </Box>
    );
};

const ModerationCard = ({post, selected, focused, onToggleSelect, onApprove, onReject, pending, translate, onOpen}) => {
    const cardRef = useRef(null);

    useEffect(() => {
        if (focused && cardRef.current) {
            cardRef.current.scrollIntoView({block: 'nearest', behavior: 'smooth'});
        }
    }, [focused]);

    return (
        <Box
            ref={cardRef}
            onClick={onOpen}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 1.5,
                borderRadius: `${t.radius.md}px`,
                border: `1px solid ${focused ? t.color.accent.primary : (selected ? t.color.accent.primary : t.color.border.default)}`,
                backgroundColor: selected ? t.color.accent.primaryTint : t.color.surface,
                boxShadow: focused ? `0 0 0 3px ${t.color.accent.primaryTint}` : 'none',
                transition: 'border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
                cursor: 'pointer',
            }}
        >
            <Checkbox
                size="small"
                checked={selected}
                onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
                onClick={(e) => e.stopPropagation()}
                sx={{p: 0.5, mt: 0.25}}
            />

            <MediaThumb post={post}/>

            <Box sx={{flex: 1, minWidth: 0}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap'}}>
                    <StatusPill variant="warning">{translate('Pending')}</StatusPill>
                    <StatusPill variant="neutral">
                        {post.message_type || String(post.media_type ?? 'UNKNOWN').toUpperCase()}
                    </StatusPill>
                    {post.is_project_post && <StatusPill variant="info">{translate('Project Post')}</StatusPill>}
                    <Box sx={{flex: 1}}/>
                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                        {moment(post.created_time).fromNow()}
                    </Typography>
                </Box>

                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.75}}>
                    <Avatar
                        src={post.creator?.avatar_thumbnail
                            ? `${process.env.REACT_APP_CDN_URL}/${post.creator.avatar_thumbnail}`
                            : undefined}
                        sx={{
                            width: 22, height: 22,
                            bgcolor: t.color.sidebarHover,
                            border: `1px solid ${t.color.border.hairline}`,
                            fontSize: 10,
                        }}
                    >
                        {post.creator?.given_name?.charAt(0)}
                    </Avatar>
                    <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.primary, fontWeight: 500}}>
                        {post.creator?.given_name} {post.creator?.family_name}
                    </Typography>
                    <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.muted}}>·</Typography>
                    <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.secondary}}>
                        {post.child?.first_name} {post.child?.last_name}
                    </Typography>
                    {post.child?.charity?.charity_name && (
                        <>
                            <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.muted}}>·</Typography>
                            <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.muted}}>
                                {post.child.charity.charity_name}
                            </Typography>
                        </>
                    )}
                </Box>

                {post.description && (
                    <Typography
                        sx={{
                            fontSize: `${t.type.body.size}px`,
                            color: t.color.text.primary,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.45,
                        }}
                    >
                        {post.description}
                    </Typography>
                )}

                {post.is_project_post && (
                    <Typography sx={{mt: 0.75, fontSize: `${t.type.caption.size}px`, color: t.color.accent.primary, fontStyle: 'italic'}}>
                        {translate('Approving duplicates this post to all children in the project.')}
                    </Typography>
                )}
            </Box>

            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0}}>
                <PrimaryButton
                    variant="primary"
                    disabled={pending}
                    startIcon={<ThumbUpAltOutlined sx={{fontSize: 14}}/>}
                    onClick={(e) => { e.stopPropagation(); onApprove(); }}
                    sx={{minHeight: 30, px: 1, fontSize: `${t.type.caption.size}px`}}
                >
                    {translate('Approve')}
                </PrimaryButton>
                <PrimaryButton
                    variant="secondary"
                    disabled={pending}
                    startIcon={<ThumbDownAltOutlined sx={{fontSize: 14}}/>}
                    onClick={(e) => { e.stopPropagation(); onReject(); }}
                    sx={{
                        minHeight: 30, px: 1, fontSize: `${t.type.caption.size}px`,
                        color: t.color.status.dangerSolid,
                    }}
                >
                    {translate('Reject')}
                </PrimaryButton>
            </Box>
        </Box>
    );
};

const rolePath = (role) => role === 'admin' ? 'admin' : role === 'umbrella' ? 'umbrellad' : 'charitad';

const ModerationPagination = ({page, perPage, total, setPage}) => {
    const pageCount = Math.max(1, Math.ceil((total ?? 0) / (perPage ?? 25)));
    if (pageCount <= 1) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                borderTop: `1px solid ${t.color.border.hairline}`,
            }}
        >
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                Showing page {page} of {pageCount} · {total ?? 0} total
            </Typography>
            <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                size="small"
                shape="rounded"
                sx={{
                    '& .MuiPaginationItem-root': {
                        fontSize: `${t.type.caption.size}px`,
                        color: t.color.text.secondary,
                        borderColor: t.color.border.default,
                    },
                    '& .Mui-selected': {
                        backgroundColor: `${t.color.accent.primaryTint} !important`,
                        color: `${t.color.accent.primary} !important`,
                    },
                }}
            />
        </Box>
    );
};

const ModerationQueueInner = () => {
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const {data, isLoading, filterValues, setFilters, displayedFilters, page, perPage, total, setPage} = useListContext();

    const [selected, setSelected] = useState([]);
    const [focusedIdx, setFocusedIdx] = useState(-1);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogAction, setDialogAction] = useState('approve');
    const [dialogTargets, setDialogTargets] = useState([]);
    const [dialogPending, setDialogPending] = useState(false);
    const [detailId, setDetailId] = useState(null);

    const posts = useMemo(() => data ?? [], [data]);
    const selectedSet = useMemo(() => new Set(selected), [selected]);
    // Looked up in the current page, so a post that leaves the queue after a refresh closes the dialog.
    const detailPost = useMemo(
        () => (detailId == null ? null : posts.find(p => String(p.id) === String(detailId)) ?? null),
        [posts, detailId],
    );
    // A refresh can drop the open post (another moderator handled it): close the detail for good,
    // so the shortcuts come back and the dialog does not reopen if the post returns.
    useEffect(() => {
        if (data && detailId != null && !detailPost) setDetailId(null);
    }, [data, detailId, detailPost]);
    const closeDetailFor = (ids) => setDetailId(prev => (prev != null && ids.includes(prev) ? null : prev));

    useEffect(() => {
        setSelected((prev) => prev.filter(id => posts.some(p => String(p.id) === String(id))));
        if (focusedIdx >= posts.length) setFocusedIdx(-1);
    }, [posts, focusedIdx]);

    const toggleSelect = (id) => {
        setSelected(prev => prev.includes(id)
            ? prev.filter(x => x !== id)
            : [...prev, id]);
    };

    const selectAll = () => {
        if (selected.length === posts.length) setSelected([]);
        else setSelected(posts.map(p => p.id));
    };

    const callEndpoint = useCallback(async (postId, action, reason) => {
        const role = permissions;
        const url = `${process.env.REACT_APP_API_URL}/api/v1/${rolePath(role)}/posts/${postId}/${action}`;
        if (action === 'reject') {
            // The reject routes read a JSON @RequestBody; the axios default (form-urlencoded) gets a 415.
            return axiosApiInstance.put(url, {reason}, {headers: {'Content-Type': 'application/json'}});
        }
        return axiosApiInstance.put(url, {});
    }, [permissions]);

    // #52: edit the text of a pending post. Resolves with the API's item (new text and translations);
    // a 409 means the post was handled or changed meanwhile, so the queue reloads.
    const saveText = (post, description) => dataProvider
        .updateDescription('posts', {id: post.id, data: {description}})
        .then(({data: item}) => {
            notify(translate('Text saved and translated'), {type: 'success'});
            refresh();
            return item;
        })
        .catch((error) => {
            if (error?.status === 409) {
                // The refresh can drop the post and close the detail, so say why outside it too.
                notify(error.message || translate('The text could not be saved'), {type: 'warning'});
                refresh();
            }
            throw error;
        });

    const runSingle = (postId, action) => {
        if (action === 'approve') {
            setDialogPending(true);
            callEndpoint(postId, 'approve', null)
                .then(() => {
                    notify(translate('Post approved successfully'), {type: 'success'});
                    refresh();
                    setSelected(prev => prev.filter(id => id !== postId));
                    closeDetailFor([postId]);
                })
                .catch(() => notify(translate('Error approving post'), {type: 'error'}))
                .finally(() => setDialogPending(false));
        } else {
            setDialogTargets([postId]);
            setDialogAction('reject');
            setDialogOpen(true);
        }
    };

    const openBulkDialog = (action) => {
        if (selected.length === 0) return;
        setDialogTargets(selected);
        setDialogAction(action);
        setDialogOpen(true);
    };

    const handleDialogConfirm = async (reason) => {
        setDialogPending(true);
        const action = dialogAction;
        const results = await Promise.allSettled(
            dialogTargets.map(id => callEndpoint(id, action, reason))
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        closeDetailFor(dialogTargets.filter((id, i) => results[i].status === 'fulfilled'));
        const succeeded = results.length - failed;
        if (succeeded > 0) {
            notify(
                action === 'approve'
                    ? `${succeeded} ${translate('approved')}${failed ? `, ${failed} ${translate('failed')}` : ''}`
                    : `${succeeded} ${translate('rejected')}${failed ? `, ${failed} ${translate('failed')}` : ''}`,
                {type: failed === 0 ? 'success' : 'warning'}
            );
        } else {
            notify(translate('Action failed'), {type: 'error'});
        }
        setDialogPending(false);
        setDialogOpen(false);
        setDialogTargets([]);
        setSelected(prev => prev.filter(id => !dialogTargets.includes(id)));
        refresh();
    };

    useEffect(() => {
        const handler = (e) => {
            if (dialogOpen || detailPost) return;
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            if (posts.length === 0) return;

            if (e.key === 'j') {
                e.preventDefault();
                setFocusedIdx(i => Math.min(posts.length - 1, i < 0 ? 0 : i + 1));
            } else if (e.key === 'k') {
                e.preventDefault();
                setFocusedIdx(i => Math.max(0, i < 0 ? 0 : i - 1));
            } else if (e.key === 'a' && focusedIdx >= 0) {
                e.preventDefault();
                runSingle(posts[focusedIdx].id, 'approve');
            } else if (e.key === 'r' && focusedIdx >= 0) {
                e.preventDefault();
                runSingle(posts[focusedIdx].id, 'reject');
            } else if (e.key === 'x' && focusedIdx >= 0) {
                e.preventDefault();
                toggleSelect(posts[focusedIdx].id);
            } else if (e.key === 'Escape') {
                setFocusedIdx(-1);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posts, focusedIdx, dialogOpen, detailPost]);

    const allSelected = selected.length > 0 && selected.length === posts.length;

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                title={translate('Moderation Queue')}
                subtitle={`${posts.length} ${posts.length === 1 ? translate('pending post') : translate('pending posts')}`}
                actions={
                    selected.length > 0 ? (
                        <>
                            <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.secondary, mr: 1}}>
                                {selected.length} {translate('selected')}
                            </Typography>
                            <PrimaryButton
                                variant="secondary"
                                startIcon={<ThumbDownAltOutlined sx={{fontSize: 14}}/>}
                                onClick={() => openBulkDialog('reject')}
                                sx={{color: t.color.status.dangerSolid}}
                            >
                                {translate('Reject selected')}
                            </PrimaryButton>
                            <PrimaryButton
                                variant="primary"
                                startIcon={<ThumbUpAltOutlined sx={{fontSize: 14}}/>}
                                onClick={() => openBulkDialog('approve')}
                            >
                                {translate('Approve selected')}
                            </PrimaryButton>
                        </>
                    ) : null
                }
            />

            <Box
                sx={{
                    backgroundColor: t.color.surface,
                    border: `1px solid ${t.color.border.default}`,
                    borderRadius: `${t.radius.md}px`,
                    mx: 3,
                    mb: 3,
                    overflow: 'hidden',
                }}
            >
                <FilterBar
                    searchValue={filterValues?.q}
                    onSearchChange={(q) => setFilters({...filterValues, q}, displayedFilters)}
                    placeholder={translate('Search pending posts')}
                    rightSlot={
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <Checkbox
                                size="small"
                                checked={allSelected}
                                indeterminate={selected.length > 0 && !allSelected}
                                onChange={selectAll}
                                sx={{p: 0.25}}
                            />
                            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                                {translate('Select all')}
                            </Typography>
                            <Box sx={{width: 1, height: 20, backgroundColor: t.color.border.default, mx: 1}}/>
                            <KeyboardOutlined sx={{fontSize: 14, color: t.color.text.muted}}/>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: t.color.text.muted,
                                    fontFamily: t.font.mono,
                                }}
                            >
                                j/k · a · r · x · esc
                            </Typography>
                        </Box>
                    }
                />

                <Box sx={{p: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                    {isLoading && posts.length === 0 && (
                        <Typography sx={{py: 4, textAlign: 'center', color: t.color.text.muted, fontSize: `${t.type.small.size}px`}}>
                            {translate('Loading…')}
                        </Typography>
                    )}
                    {!isLoading && posts.length === 0 && (
                        <EmptyState
                            icon={<TaskAltOutlined sx={{fontSize: 32, color: t.color.status.successSolid}}/>}
                            title={translate('Inbox zero')}
                            description={translate('No pending posts to moderate. Nice work.')}
                        />
                    )}
                    {posts.map((post, i) => (
                        <ModerationCard
                            key={post.id}
                            post={post}
                            selected={selectedSet.has(post.id)}
                            focused={focusedIdx === i}
                            onToggleSelect={() => toggleSelect(post.id)}
                            onOpen={() => { setFocusedIdx(i); setDetailId(post.id); }}
                            onApprove={() => runSingle(post.id, 'approve')}
                            onReject={() => runSingle(post.id, 'reject')}
                            pending={dialogPending}
                            translate={translate}
                        />
                    ))}
                </Box>

                <ModerationPagination page={page} perPage={perPage} total={total} setPage={setPage}/>
            </Box>

            <ModerationPostDialog
                post={detailPost}
                onClose={() => setDetailId(null)}
                onSaveText={saveText}
                onApprove={() => detailPost && runSingle(detailPost.id, 'approve')}
                onReject={() => {
                    if (!detailPost) return;
                    // Release focus before the reason dialog opens; MUI hides this dialog from assistive
                    // technology then, and a focused element inside it logs an aria-hidden warning.
                    document.activeElement?.blur?.();
                    runSingle(detailPost.id, 'reject');
                }}
                pending={dialogPending}
            />

            <ApprovalDialog
                open={dialogOpen}
                action={dialogAction}
                onClose={() => { setDialogOpen(false); setDialogTargets([]); }}
                onConfirm={handleDialogConfirm}
                pending={dialogPending}
                bulkCount={dialogTargets.length}
            />
        </Box>
    );
};

const ModerationQueueStatamic = (props) => (
    <List
        {...props}
        sort={{field: 'created_time', order: 'DESC'}}
        perPage={25}
        actions={false}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': {backgroundColor: t.color.page},
        }}
    >
        <ModerationQueueInner/>
    </List>
);

export default ModerationQueueStatamic;
